<template>
    <TransitionRoot as="template" :show="visible">
        <Dialog as="div" class="relative z-50" @close="noop">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                            <div>
                                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                                    <font-awesome-icon :icon="faFileContract" class="h-6 w-6 text-blue-500" aria-hidden="true" />
                                </div>
                                <div class="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                                        We've updated our Terms of Service
                                    </DialogTitle>
                                    <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                            Please review and accept the updated
                                            <a :href="route('terms.show')" target="_blank" class="text-blue-600 underline hover:text-blue-800">
                                                Terms of Service
                                            </a>
                                            to continue using RentRisk.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div v-if="error" class="text-red-600 text-sm mt-4">
                                {{ error }}
                            </div>

                            <div class="mt-5 sm:mt-6">
                                <button
                                    type="button"
                                    class="primaryButton w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    :disabled="isSubmitting"
                                    @click="accept"
                                >
                                    {{ isSubmitting ? 'Processing...' : 'Accept & Continue' }}
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { ref, computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faFileContract } from '@fortawesome/pro-light-svg-icons';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';

const page = usePage();

const accepted = ref(false);
const error = ref(null);
const isSubmitting = ref(false);

const visible = computed(() =>
    page.props.needs_tos_reacceptance === true
    && !page.props.impersonating
    && !accepted.value
);

function noop() {
    // Blocking modal: Esc and backdrop clicks must not dismiss it.
}

function accept() {
    error.value = null;
    isSubmitting.value = true;

    // See TermsAcceptancePopup.vue for why axios rather than Inertia's
    // router.post: its Promise catches network-level failures (not just
    // 4xx/5xx), which matters for an acceptance that feeds the audit trail.
    axios.post(route('terms.reaccept'))
        .then(() => {
            isSubmitting.value = false;
            accepted.value = true;
        })
        .catch((e) => {
            isSubmitting.value = false;
            if (e.response) {
                const data = e.response.data;
                error.value = (data && (data.message || data.error)) || 'Failed to accept terms. Please try again.';
            } else if (e.request) {
                error.value = 'Network error — please check your connection and tap Accept again.';
            } else {
                error.value = 'Unexpected error. Please try again.';
            }
            console.error('ToS re-acceptance error:', e);
        });
}
</script>
